import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { PresaveFansSummary } from 'fym-shared/src/smartlinks/presaveSubscriptions';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
import { IntegrationTypeIcon } from '../components/IntegrationTypeIcon/IntegrationTypeIcon';
import { formatServiceName } from '../../smartLinks/stats/formatServiceName';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansServiceSplitProps {
  summary: PresaveFansSummary;
}

interface SplitRow {
  key: string;
  integrationType: string | null;
  name: string;
  count: number;
}

/**
 * "Where they listen": one row per service, most fans first, then "Email only" (design c1). The
 * service mark and the name carry the identity; the bar length is the share of the largest row.
 */
const FansServiceSplitComponent: React.FC<FansServiceSplitProps> = ({ summary }) => {
  const intl = useIntl();

  const rows = useMemo<SplitRow[]>(
    () => [
      ...[...summary.byService]
        .sort((a, b) => b.count - a.count)
        .map((row) => ({
          key: row.integrationType,
          integrationType: row.integrationType,
          name: formatServiceName(row.integrationType),
          count: row.count,
        })),
      {
        key: 'emailOnly',
        integrationType: null,
        name: intl.formatMessage(messages.splitEmailOnly),
        count: summary.emailOnly,
      },
    ],
    [intl, summary.byService, summary.emailOnly]
  );

  const peak = useMemo(() => rows.reduce((max, row) => Math.max(max, row.count), 0), [rows]);

  const items = useMemo(
    () =>
      rows.map((row) => (
        <div key={row.key} className={clsx(styles.splitRow, row.integrationType === null && styles.splitMuted)}>
          <span className={styles.splitMark}>
            {row.integrationType === null ? null : (
              <IntegrationTypeIcon integrationType={row.integrationType} size={16} />
            )}
          </span>
          <span className={styles.splitName}>{row.name}</span>
          <span className={styles.splitTrack}>
            {row.count > 0 && peak > 0 ? (
              <span className={styles.splitFill} style={{ width: `${Math.max(4, (row.count / peak) * 100)}%` }} />
            ) : null}
          </span>
          <span className={styles.splitValue}>{intl.formatNumber(row.count)}</span>
        </div>
      )),
    [intl, peak, rows]
  );

  return (
    <Panel>
      <PanelHead
        title={intl.formatMessage(messages.splitTitle)}
        right={<span className={styles.meta}>{intl.formatMessage(messages.splitMeta, { count: summary.total })}</span>}
      />
      <PanelBody>
        <div className={styles.split}>{items}</div>
        <p className={styles.note}>{intl.formatMessage(messages.splitNote)}</p>
      </PanelBody>
    </Panel>
  );
};

export const FansServiceSplit = TypedMemo(FansServiceSplitComponent);
